import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/shipping-and-payment-options/{orderToken}';
const HTTP_METHOD = 'get';

export type MakeListShippingAndPaymentsForOrderDeps<ShipOpts extends Schema.Top> = {
  readonly shippingOptions: ShipOpts;
};

export const makeListShippingAndPaymentsForOrder = <ShipOpts extends Schema.Top>(
  deps: MakeListShippingAndPaymentsForOrderDeps<ShipOpts>
) => {
  const ListShippingAndPaymentsForOrderError = createBackendApiError({
    domain: 'ShippingAndPayment',
    endpoint: 'ListForOrder',
    path: ENDPOINT,
    identifierField: 'zoneCode',
  } as const);

  const getShippingAndPaymentsForOrderFetch = (zoneCode: string, orderToken: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ query: { zone: zoneCode }, params: { orderToken } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'shippingAndPayment.zoneCode': zoneCode,
        },
      })
    );

  const listShippingAndPaymentsForOrder = (zoneCode: string, orderToken: string) =>
    getShippingAndPaymentsForOrderFetch(zoneCode, orderToken).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.shippingOptions))(response),
          404: (response) =>
            Effect.fail(
              new ListShippingAndPaymentsForOrderError.NotFoundError({ zoneCode, response })
            ),
          orElse: (response) =>
            Effect.fail(
              new ListShippingAndPaymentsForOrderError.ApiRequestError({ zoneCode, response })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.OrderShippingAndPaymentsList,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} shipping options for zone: ${zoneCode}`
        )
      ),
      Effect.withSpan('api.orderShippingAndPayments.list', {
        kind: 'internal',
        attributes: {
          'shippingAndPayment.zoneCode': zoneCode,
        },
      })
    );

  return { listShippingAndPaymentsForOrder, ListShippingAndPaymentsForOrderError };
};
